import React, { useState } from "react";
import styles from "./MediaUploader.module.css";
import {
	primaryTextColor,
	secondaryTextColor,
	fw400,
	titleS,
} from "../../../Global.module.css";
import Loader from "../../reusable/Loader/Loader";
import SnackBar from "../../reusable/SnackBar/SnackBar";
import embedOptions from "../EmbedOptions.json";
import { createClient } from "pexels";
import axios from "axios";
import SearchBox from "../../reusable/SearchBox/SearchBox";

const client = createClient(process.env.REACT_APP_PEXEL_API);

export default function MediaUploader({
	mediaSelected,
	closeUploader,
	fullSize,
}) {
	const [state, setState] = useState({
		searchQuery: "",
		photos: [],
		isLoading: false,
		currentPage: 0,
	});
	const [embed, setEmbed] = useState("");
	const [embedLink, setEmbedLink] = useState("");
	const [nextPage, setNextPage] = useState("");
	const [error, setError] = useState(false);
	const photosPerPage = 12;

	const handleSearchQuery = (e) => {
		setState({
			...state,
			searchQuery: e.target.value,
		});
	};

	const populatePhotos = (photosList, reset = false) => {
		return new Promise((resolve, reject) => {
			if (photosList.length) {
				let photos = reset ? [] : state.photos;
				for (let i = 0; i < photosList.length; i++) {
					photosList[i] = {
						src: fullSize
							? photosList[i].src.landscape
							: photosList[i].src.large,
						preview: photosList[i].src.tiny,
					};
				}
				photos.push(photosList);
				setState({
					...state,
					currentPage: reset ? 0 : state.currentPage,
					photos,
				});
			}
			resolve(true);
		});
	};

	const onSearch = () => {
		if (state.searchQuery !== "") {
			setState({
				...state,
				isLoading: true,
			});
			client.photos
				.search({ query: state.searchQuery, per_page: photosPerPage })
				.then(async (data) => {
					setState({
						...state,
						isLoading: false,
					});
					setNextPage(data.next_page);
					await populatePhotos(data.photos, true);
				});
		}
	};

	const goBack = () => {
		if (state.currentPage > 0) {
			setState({
				...state,
				currentPage: state.currentPage - 1,
			});
		}
	};

	const showMore = () => {
		if (state.photos.length > state.currentPage + 1) {
			setState({
				...state,
				currentPage: state.currentPage + 1,
			});
		} else if (nextPage) {
			setState({
				...state,
				isLoading: true,
			});
			axios
				.get(nextPage, {
					headers: {
						Authorization: process.env.REACT_APP_PEXEL_API,
					},
				})
				.then(async (res) => {
					await populatePhotos(res.data.photos);
					setState({
						...state,
						isLoading: false,
						currentPage: state.currentPage + 1,
					});
					setNextPage(res.data.next_page);
				})
				.catch((e) => {
					setState({
						...state,
						isLoading: false,
					});
				});
		}
	};

	const photoSelected = (src) => {
		mediaSelected(embedOptions.Image, src);
		closeUploader();
	};

	const videoSelected = (src) => {
		mediaSelected(embedOptions.Video, src);
		closeUploader();
	};

	const handleReaderLoaded = (readerEvt) => {
		photoSelected(readerEvt.target.result);
	};

	const handleFiles = async (e) => {
		let file = e.target.files[0];
		if (file) {
			if (file.size > 0.5 * 1024 * 1024) {
				setError({
					message: "File size shouldn't be greater than 3MB",
					severity: "error",
				});
				return;
			}
			const reader = new FileReader();
			reader.onload = handleReaderLoaded.bind(this);
			reader.readAsDataURL(file);
		}
	};

	const closeSnackBar = () => {
		setError(false);
	};

	const handleEnterSearch = (e) => {
		if (
			e.keyCode === 13 ||
			e.key === "Enter" ||
			e.code === "Enter" ||
			e.code === "NumpadEnter"
		) {
			onSearch();
		}
	};

	const uploadImage = () => {
		document.getElementById("fileInput").click();
	};

	const showPhotos = () => {
		let photosList = state.photos[state.currentPage];
		let photos = [];
		for (let i = 0; i < photosList.length; i++) {
			photos.push(
				<img
					key={i}
					width="150"
					height="150"
					src={photosList[i].preview}
					alt=""
					onClick={() => {
						photoSelected(photosList[i].src);
					}}
				/>
			);
		}
		return photos;
	};

	const renderOption = (option) => {
		switch (option) {
			case embedOptions.Image:
				return (
					<div className={styles.embedInput}>
						<input
							type="text"
							autoComplete="off"
							placeholder="Paste Image URL"
							value={embedLink}
							onChange={(e) => setEmbedLink(e.target.value)}
						/>
						<p
							className={`${styles.done} ${fw400} ${titleS}`}
							onClick={() => {
								photoSelected(embedLink);
							}}
						>
							Done
						</p>
					</div>
				);

			case embedOptions.Video:
				return (
					<div className={styles.embedInput}>
						<input
							type="text"
							autoComplete="off"
							placeholder="Paste Video URL (YouTube, Vimeo)"
							value={embedLink}
							onChange={(e) => setEmbedLink(e.target.value)}
						/>
						<p
							className={`${styles.done} ${fw400} ${titleS}`}
							onClick={() => {
								videoSelected(embedLink);
							}}
						>
							Done
						</p>
					</div>
				);

			default:
				return (
					<div>
						<SearchBox
							value={state.searchQuery}
							onChange={handleSearchQuery}
							onEnter={handleEnterSearch}
							isLoading={state.isLoading}
							onSearch={onSearch}
						/>
						<div className={styles.photos}>
							{state.photos.length > 0 ? showPhotos() : null}
						</div>
						{state.photos.length ? (
							<div className={styles.actionBtnContainer}>
								<p
									className={`${styles.actionBtn} ${secondaryTextColor} ${fw400} ${titleS}`}
									onClick={goBack}
								>
									{state.isLoading ? null : "Back"}
								</p>

								<p
									className={`${styles.actionBtn} ${secondaryTextColor} ${fw400} ${titleS}`}
									onClick={showMore}
								>
									{state.isLoading ? <Loader /> : "Show More"}
								</p>
							</div>
						) : null}
					</div>
				);
		}
	};

	return (
		<div className={styles.mediaUploader}>
			<p
				className={`${styles.closeBtn} ${secondaryTextColor} ${fw400} ${titleS}`}
				onClick={closeUploader}
			>
				X
			</p>

			<div
				className={`${styles.optionList} ${primaryTextColor} ${fw400} ${titleS}`}
			>
				<p className={styles.option} onClick={uploadImage}>
					Upload Image
				</p>
				<input
					className={styles.fileInput}
					type="file"
					name="image"
					id="fileInput"
					accept=".jpeg, .png, .jpg"
					onChange={handleFiles}
				/>
				<p className={styles.option} onClick={() => setEmbed("")}>
					Search Image
				</p>
				<p
					className={styles.option}
					onClick={() => setEmbed(embedOptions.Image)}
				>
					Embed Image
				</p>
				<p
					className={styles.option}
					onClick={() => setEmbed(embedOptions.Video)}
				>
					Embed Video
				</p>
			</div>
			<div className={styles.contentBox}>{renderOption(embed)}</div>
			<SnackBar
				open={error}
				handleClose={closeSnackBar}
				message={error.message}
				severity={error.severity}
			/>
		</div>
	);
}
